'use client';

import { useClearProposalVote, useSetProposalVote } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type { MouseEvent } from 'react';

import { TOAST_IDS } from '@/config/toast-ids';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { deriveBaseNetScore } from '@/lib/launchpad/derive-fake-fields';
import { showToast } from '@/lib/ui/toast';
import { useLaunchpadVotesStore } from '@/stores/launchpad-votes';

interface ProposalVoteModel {
  reaction: 'up' | 'down' | null;
  upCount: number;
  downCount: number;
  upAriaLabel: string;
  downAriaLabel: string;
  onReact: (next: 'up' | 'down', e: MouseEvent<HTMLButtonElement>) => void;
}

/**
 * Live counts threaded from the SDK row. When present, this hook drives the
 * vote button off real server-authoritative data + SDK mutations
 * (`useSetProposalVote` / `useClearProposalVote`). The manifest's optimistic
 * recipe patches `myVote` instantly; the NATS `votes.v0` realtime broadcast
 * then patches `upvotes` + `downvotes` on every browser within ~100 ms.
 *
 * Detail-page callers (`proposal-header.tsx`) don't yet pass this argument
 * and fall back to the legacy Zustand store + `deriveBaseNetScore` fake.
 * That fallback path is deleted when the detail page migrates to v2.
 */
interface LiveCounts {
  upvotes: number;
  downvotes: number;
  myVote: 'up' | 'down' | null;
}

function splitBaseCounts(baseNet: number): { upCount: number; downCount: number } {
  if (baseNet >= 0) return { upCount: baseNet, downCount: 0 };
  return { upCount: 0, downCount: Math.abs(baseNet) };
}

export function useProposalVoteModel(
  proposalPubkey: string,
  liveCounts?: LiveCounts,
): ProposalVoteModel {
  const t = useTranslations('launchpad.feed.vote');
  const { ensureAuthenticated } = useAuthGate();
  const setVote = useSetProposalVote();
  const clearVote = useClearProposalVote();
  // Legacy local-only store — drives the detail page until it migrates.
  const legacyReaction = useLaunchpadVotesStore((s) => s.getVote(proposalPubkey));
  const setLegacyVote = useLaunchpadVotesStore((s) => s.setVote);

  const isLiveMode = liveCounts !== undefined;
  const reaction = isLiveMode ? liveCounts.myVote : legacyReaction;

  let upCount: number;
  let downCount: number;
  if (isLiveMode) {
    upCount = liveCounts.upvotes;
    downCount = liveCounts.downvotes;
  } else {
    const base = deriveBaseNetScore(proposalPubkey);
    const baseCounts = splitBaseCounts(base);
    upCount = baseCounts.upCount + (legacyReaction === 'up' ? 1 : 0);
    downCount = baseCounts.downCount + (legacyReaction === 'down' ? 1 : 0);
  }
  const net = upCount - downCount;

  function onReact(next: 'up' | 'down', e: MouseEvent<HTMLButtonElement>): void {
    // Vote controls live inside clickable rows/cards; stop parent navigation.
    e.preventDefault();
    e.stopPropagation();
    // Logged-out users can't vote — open the auth gate instead of mutating.
    if (!ensureAuthenticated()) return;
    if (isLiveMode) {
      const onError = (): void => {
        showToast({
          id: TOAST_IDS.PROPOSAL_VOTE_ERROR,
          message: t('toastError'),
          variant: 'error',
        });
      };
      if (reaction === next) {
        clearVote.mutate(
          { pubkey: proposalPubkey },
          {
            onSuccess: () => {
              showToast({
                id: TOAST_IDS.PROPOSAL_VOTE_CLEARED,
                message: t('toastCleared'),
                variant: 'success',
              });
            },
            onError,
          },
        );
      } else {
        setVote.mutate(
          { pubkey: proposalPubkey, kind: next },
          {
            onSuccess: () => {
              showToast({
                id: TOAST_IDS.PROPOSAL_VOTED,
                message: t(next === 'up' ? 'toastUp' : 'toastDown'),
                variant: 'success',
              });
            },
            onError,
          },
        );
      }
      return;
    }
    if (legacyReaction === next) {
      setLegacyVote(proposalPubkey, null);
    } else {
      setLegacyVote(proposalPubkey, next);
    }
  }

  return {
    reaction,
    upCount,
    downCount,
    upAriaLabel: t('aria_up', { count: net }),
    downAriaLabel: t('aria_down', { count: net }),
    onReact,
  };
}
